import Image from 'next/image';
import { Icon as IconType } from '@/types/icon';
import { processIcon, hasValidIcon, getIconAlt } from '@/utils/icon';

interface IconProps {
  icon: IconType | undefined | null;
  width: number;
  height: number;
  alt?: string;
  className?: string;
  onClick?: () => void;
  showArrow?: boolean;
}
export default function Icon({
  icon,
  width,
  height,
  alt,
  className = '',
  onClick,
  showArrow = false,
}: IconProps) {
  // Don't render anything if icon is invalid
  if (!hasValidIcon(icon)) {
    return null;
  }

  const iconSrc = processIcon(icon);
  const iconAlt = alt || getIconAlt(icon);

  const image = (
    <Image
      src={iconSrc}
      // When the arrow cue is shown the icon lives inside a labeled, clickable
      // cell, so the image itself is decorative and hidden from screen readers.
      alt={showArrow ? "" : iconAlt}
      aria-hidden={showArrow ? true : undefined}
      width={width}
      height={height}
      className={className}
      onClick={onClick}
    />
  );

  if (!showArrow) {
    return image;
  }

  // "Opens link" cue: arrow just to the right of the icon, org brand color.
  // Absolutely positioned so it doesn't add width and shift the icon off-center
  // (which would misalign a label rendered below the icon). aria-hidden so it
  // doesn't affect the parent link's accessible name.
  return (
    <span className="relative inline-flex items-center leading-none">
      {image}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute left-full top-1/2 ml-1 -translate-y-1/2 text-[color:var(--main-color,#0B313F)]"
      >
        <svg
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <line x1="7" y1="17" x2="17" y2="7" />
          <polyline points="7 7 17 7 17 17" />
        </svg>
      </span>
    </span>
  );
}